import { ButtonGroup, Menu } from '@mui/material';
import { ChevronDownIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { AppLinkOptions, asLinkProps } from '../../routing/links/linkOptions';
import { colorSystem } from '../../theme';
import type { IconElement } from '../icons/IconElement';
import { MenuItem, MenuItemProps } from '../MenuItem/MenuItem';
import { Tooltip } from '../Tooltip/Tooltip';
import { ButtonSize } from './BaseButton';
import { PrimaryButton } from './PrimaryButton';

type SplitButtonOptionBase = {
    id: string;
    label: string | JSX.Element;
    // JSXElementConstructor checks only how the item is called, so a forwardRef menu item fits as it is.
    overrides?: { MenuItem?: React.JSXElementConstructor<MenuItemProps> };
    hidden?: boolean;
    tooltip?: string;
    startIcon?: IconElement;
    badge?: JSX.Element;
};

/** Runs `onClick` or links to `link`; a link option takes no `setOption` and ignores `overrides.MenuItem`. */
export type SplitButtonOption = SplitButtonOptionBase &
    (
        | { onClick: () => void; setOption?: (option: SplitButtonOption) => void; link?: never }
        | { link: AppLinkOptions; onClick?: never; setOption?: never }
    );

interface SplitButtonProps {
    options: SplitButtonOption[];
    selectedKey?: string;
    overrides?: {
        button?: {
            /** Replaces `PrimaryButton`; with `link` options it must be a design-system linkable button. */
            Component?: React.ComponentType;
            size?: ButtonSize;
        };
        menu?: { Component?: React.ComponentType<{ open: boolean }> };
    };
    disabled?: boolean;
    pinnedSelectedOption?: SplitButtonOption;
    dataTrackingId?: string;
    isLoading?: boolean;
}

export const SplitButton = ({
    options,
    overrides,
    disabled,
    selectedKey,
    pinnedSelectedOption,
    dataTrackingId,
    isLoading,
}: SplitButtonProps): JSX.Element => {
    const [isOpen, setIsOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const visibleOptions = options.filter((o) => !o.hidden);
    const selectedOption =
        pinnedSelectedOption ??
        (visibleOptions.find((option) => {
            return option.id === selectedKey;
        }) ||
            visibleOptions[0]);

    const handleToggle = () => {
        setIsOpen((prevOpen) => !prevOpen);
    };

    const CustomButton = overrides?.button?.Component ?? PrimaryButton;
    const size = overrides?.button?.size ?? 'large';

    const CustomMenu = overrides?.menu?.Component ?? Menu;

    const selectedButtonProps = {
        id: selectedOption.id,
        size,
        disableElevation: true,
        disabled,
        isLoading,
        startIcon: selectedOption.startIcon,
        style: { whiteSpace: 'nowrap', borderRight: `1px solid ${colorSystem.neutral[3]}` } as const,
        'data-trackingid': dataTrackingId,
    };

    return (
        <>
            <ButtonGroup variant="contained" ref={anchorRef} color={'inherit'} style={{ boxShadow: 'none' }}>
                <Tooltip title={selectedOption.tooltip || ''} placement="top">
                    {selectedOption.link !== undefined ? (
                        <CustomButton {...selectedButtonProps} {...asLinkProps(selectedOption.link)}>
                            {selectedOption.label}
                        </CustomButton>
                    ) : (
                        <CustomButton {...selectedButtonProps} onClick={selectedOption.onClick}>
                            {selectedOption.label}
                        </CustomButton>
                    )}
                </Tooltip>
                {visibleOptions.length > 1 && (
                    <CustomButton
                        aria-controls={isOpen ? 'split-button-menu' : undefined}
                        aria-expanded={isOpen ? 'true' : undefined}
                        aria-haspopup="menu"
                        onClick={handleToggle}
                        size={size}
                        style={{ minWidth: '40px', margin: 0, padding: 0, borderLeft: '0px' }}
                        disableElevation
                        disabled={disabled}
                    >
                        <ChevronDownIcon size={24} />
                    </CustomButton>
                )}
            </ButtonGroup>

            <CustomMenu open={isOpen} anchorEl={anchorRef.current} onClose={handleToggle}>
                {visibleOptions.map((option) => {
                    const common = {
                        id: option.id,
                        selected: selectedOption.id === option.id,
                        startIcon: option.startIcon,
                        label: option.label,
                        badge: option.badge,
                    };
                    if (option.link !== undefined) {
                        return (
                            <Tooltip key={option.id} title={option.tooltip || ''} placement="right">
                                <span>
                                    <MenuItem
                                        {...common}
                                        {...asLinkProps(option.link)}
                                        onClick={() => setIsOpen(false)}
                                    />
                                </span>
                            </Tooltip>
                        );
                    }
                    const Item = option.overrides?.MenuItem ?? MenuItem;
                    return (
                        <Tooltip key={option.id} title={option.tooltip || ''} placement="right">
                            <span>
                                <Item
                                    {...common}
                                    onClick={() => {
                                        option.setOption ? option.setOption(option) : option.onClick();
                                        setIsOpen(false);
                                    }}
                                />
                            </span>
                        </Tooltip>
                    );
                })}
            </CustomMenu>
        </>
    );
};
